import {
  type Abrechnung,
  abrechnungenSchema,
  type EditionAendern,
  type EditionAnlegen,
  type EditionPreis,
  type EditionPreisAnlegen,
  editionPreisSchema,
  type Monatsmengen,
  monatsmengenSchema,
  type PartnerAendern,
  type PartnerAnlegen,
  type PartnerDetailUebersicht,
  type PartnerKatalog,
  type PartnerUebersicht,
  type Preisabweichung,
  type PreisabweichungAnlegen,
  partnerDetailUebersichtSchema,
  partnerKatalogSchema,
  partnerUebersichtListeSchema,
  preisabweichungSchema,
  type ServerKopplungscode,
  serverKopplungscodeSchema,
  type Tagesstaende,
  tagesstaendeSchema,
  type VerwaltungAuftrag,
  type VerwaltungEdition,
  type VerwaltungPartner,
  type VerwaltungSitzung,
  verwaltungAuftraegeSchema,
  verwaltungEditionenSchema,
  verwaltungEditionSchema,
  verwaltungPartnerListeSchema,
  verwaltungPartnerSchema,
  verwaltungSitzungSchema,
} from '@nac2s/shared'
import { apiFetch } from './client.js'

/** Endpunkte der Verwaltung — Antworten geprueft gegen den Vertrag, nicht nur getypt. */

export async function ladeSitzung(): Promise<VerwaltungSitzung> {
  return verwaltungSitzungSchema.parse(await apiFetch('/verwaltung/api/sitzung'))
}

export async function anmelden(email: string, passwort: string): Promise<void> {
  await apiFetch('/verwaltung/auth/sign-in/email', {
    method: 'POST',
    body: JSON.stringify({ email, password: passwort }),
  })
}

export async function abmelden(): Promise<void> {
  await apiFetch('/verwaltung/auth/sign-out', { method: 'POST', body: '{}' })
}

export async function ladePartner(): Promise<VerwaltungPartner[]> {
  return verwaltungPartnerListeSchema.parse(await apiFetch('/verwaltung/api/partner')).partner
}

export async function ladeEinenPartner(id: string): Promise<VerwaltungPartner> {
  return verwaltungPartnerSchema.parse(await apiFetch(`/verwaltung/api/partner/${id}`))
}

export async function partnerAnlegen(eingabe: PartnerAnlegen): Promise<VerwaltungPartner> {
  return verwaltungPartnerSchema.parse(
    await apiFetch('/verwaltung/api/partner', { method: 'POST', body: JSON.stringify(eingabe) }),
  )
}

export async function partnerAendern(
  id: string,
  aenderung: PartnerAendern,
): Promise<VerwaltungPartner> {
  return verwaltungPartnerSchema.parse(
    await apiFetch(`/verwaltung/api/partner/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(aenderung),
    }),
  )
}

/** Den offenen Code jetzt beim Stack einloesen statt auf den Takt zu warten. */
export async function serverKoppeln(
  id: string,
): Promise<{ status: string; fehler: string | null }> {
  return (await apiFetch(`/verwaltung/api/partner/${id}/server/koppeln`, {
    method: 'POST',
    body: '{}',
  })) as { status: string; fehler: string | null }
}

export async function serverAbfragen(
  id: string,
): Promise<{ status: string; fehler: string | null }> {
  return (await apiFetch(`/verwaltung/api/partner/${id}/server/abfragen`, {
    method: 'POST',
    body: '{}',
  })) as { status: string; fehler: string | null }
}

export async function editionPreisAnlegen(
  key: string,
  eingabe: EditionPreisAnlegen,
): Promise<EditionPreis> {
  return editionPreisSchema.parse(
    await apiFetch(`/verwaltung/api/editionen/${key}/preise`, {
      method: 'POST',
      body: JSON.stringify(eingabe),
    }),
  )
}

export async function abweichungAnlegen(
  partnerId: string,
  eingabe: PreisabweichungAnlegen,
): Promise<Preisabweichung> {
  return preisabweichungSchema.parse(
    await apiFetch(`/verwaltung/api/partner/${partnerId}/abweichungen`, {
      method: 'POST',
      body: JSON.stringify(eingabe),
    }),
  )
}

export async function abweichungLoeschen(partnerId: string, id: string): Promise<void> {
  await apiFetch(`/verwaltung/api/partner/${partnerId}/abweichungen/${id}`, { method: 'DELETE' })
}

/** Plattform-Domain der Verwaltung — daraus folgen die Subdomains der Partner. */
export async function ladePlattform(): Promise<{ plattformDomain: string | null }> {
  return apiFetch('/verwaltung/api/plattform')
}

export async function ladeMengen(id: string, monat: string): Promise<Monatsmengen> {
  return monatsmengenSchema.parse(
    await apiFetch(`/verwaltung/api/partner/${id}/mengen?monat=${monat}`),
  )
}

export async function ladeTagesstaende(id: string, monat: string): Promise<Tagesstaende> {
  return tagesstaendeSchema.parse(
    await apiFetch(`/verwaltung/api/partner/${id}/mengen/tage?monat=${monat}`),
  )
}

export async function mengenAbrufen(
  id: string,
): Promise<{ status: 'ok' | 'fehler'; kunden: number; fehler: string | null }> {
  return apiFetch(`/verwaltung/api/partner/${id}/abruf`, { method: 'POST', body: '{}' })
}

export async function ladeAbrechnung(monat: string): Promise<Abrechnung[]> {
  return abrechnungenSchema.parse(await apiFetch(`/verwaltung/api/abrechnung/${monat}`))
    .abrechnungen
}

export function abrechnungCsvAdresse(monat: string): string {
  return `/verwaltung/api/abrechnung/${monat}/csv`
}

export async function ladeAuftraege(): Promise<{
  auftraege: VerwaltungAuftrag[]
  nichtErreichbar: Array<{ partnerId: string; partnerName: string }>
}> {
  return verwaltungAuftraegeSchema.parse(await apiFetch('/verwaltung/api/auftraege'))
}

export async function auftragEntscheiden(
  partnerId: string,
  auftragId: string,
  entscheidung: 'freigeben' | 'loeschen',
): Promise<void> {
  await apiFetch(`/verwaltung/api/auftraege/${partnerId}/${auftragId}/${entscheidung}`, {
    method: 'POST',
    body: '{}',
  })
}

// --- Editionen und Kataloge (ADR-0014) ----------------------------------------

export async function ladeEditionen(): Promise<VerwaltungEdition[]> {
  return verwaltungEditionenSchema.parse(await apiFetch('/verwaltung/api/editionen')).editionen
}

export async function editionAnlegen(eingabe: EditionAnlegen): Promise<VerwaltungEdition> {
  return verwaltungEditionSchema.parse(
    await apiFetch('/verwaltung/api/editionen', { method: 'POST', body: JSON.stringify(eingabe) }),
  )
}

export async function editionAendern(
  key: string,
  aenderung: EditionAendern,
): Promise<VerwaltungEdition> {
  return verwaltungEditionSchema.parse(
    await apiFetch(`/verwaltung/api/editionen/${key}`, {
      method: 'PATCH',
      body: JSON.stringify(aenderung),
    }),
  )
}

export async function editionLoeschen(key: string): Promise<void> {
  await apiFetch(`/verwaltung/api/editionen/${key}`, { method: 'DELETE' })
}

export async function ladeKatalog(partnerId: string): Promise<PartnerKatalog> {
  return partnerKatalogSchema.parse(await apiFetch(`/verwaltung/api/partner/${partnerId}/katalog`))
}

export async function katalogSetzen(
  partnerId: string,
  editionen: string[],
  mindestlaufzeit: Record<string, number>,
): Promise<PartnerKatalog & { verteilung: { status: 'ok' | 'fehler'; fehler: string | null } }> {
  return apiFetch(`/verwaltung/api/partner/${partnerId}/katalog`, {
    method: 'PUT',
    body: JSON.stringify({ editionen, mindestlaufzeit }),
  })
}

export async function katalogVerteilen(
  partnerId: string,
): Promise<{ status: 'ok' | 'fehler'; fehler: string | null }> {
  return apiFetch(`/verwaltung/api/partner/${partnerId}/katalog/verteilen`, {
    method: 'POST',
    body: '{}',
  })
}

// --- Uebersichten (ADR-0015 Nachtrag) --------------------------------------

export async function ladeUebersicht(): Promise<{ monat: string; partner: PartnerUebersicht[] }> {
  return partnerUebersichtListeSchema.parse(await apiFetch('/verwaltung/api/uebersicht'))
}

export async function ladePartnerUebersicht(id: string): Promise<PartnerDetailUebersicht> {
  return partnerDetailUebersichtSchema.parse(
    await apiFetch(`/verwaltung/api/partner/${id}/uebersicht`),
  )
}

/** Server-Kopplungscode (ADR-0020) — erscheint genau einmal, gilt 24 Stunden. */
export async function kopplungscodeErzeugen(id: string): Promise<ServerKopplungscode> {
  return serverKopplungscodeSchema.parse(
    await apiFetch(`/verwaltung/api/partner/${id}/kopplungscode`, { method: 'POST', body: '{}' }),
  )
}

export async function serverAbmelden(id: string): Promise<void> {
  await apiFetch(`/verwaltung/api/partner/${id}/server/abmelden`, { method: 'POST', body: '{}' })
}
